// Mock data for The Bufkin Law Firm landing page

import {
  Scale,
  Calculator,
  ShieldAlert,
  Gavel,
  FileSearch,
  Building2,
  Users,
  TrendingUp,
  BookOpen,
} from "lucide-react";

export const FIRM = {
  name: "The Bufkin Law Firm",
  tagline: "Forensic Accounting & Financially Oriented Legal Matters",
  attorney: "Richard L. Bufkin",
  nickname: "Dick",
  phone: "(214) 415-1759",
  phoneSecondary: "(972) 808-9791",
  email: "dick@bufkinlaw.com",
  address: {
    line1: "126 W. Second Street",
    line2: "Mt. Pleasant, Texas 75455",
  },
  hours: "Weekdays 9:00 am – 5:00 pm",
  hoursNote: "Evenings and weekends by appointment.",
  yearsExperience: "50+",
  region: "Dallas–Fort Worth & Texas",
};

export const CREDENTIALS = [
  "Attorney at Law",
  "Certified Public Accountant",
  "AICPA Certified in Financial Forensics (CFF)",
  "Master of Laws in Taxation — SMU",
  "MBA",
];

// FAQ content for AEO (Answer Engine Optimization) — answers shaped for
// citation by Google AI Overviews, ChatGPT, Perplexity, etc.
export const FAQS = [
  {
    q: "What does The Bufkin Law Firm specialize in?",
    a: "The Bufkin Law Firm specializes in forensic accounting and financially oriented legal matters. The firm handles business litigation, family-law litigation with financial issues, probate disputes, bankruptcy trustee representation, fraud detection, economic-damages claims, and expert witness services. It does not handle criminal defense or personal injury cases.",
  },
  {
    q: "Who is Richard L. Bufkin?",
    a: "Richard L. Bufkin — known as Dick — is the founding attorney of The Bufkin Law Firm. He is a Texas attorney, Certified Public Accountant (CPA), and AICPA-certified forensic accountant (CFF) with over 50 years of experience. He also holds a Master of Laws in Taxation (LL.M.) from SMU and an MBA. He is one of very few attorneys nationwide holding the AICPA Certified in Financial Forensics credential.",
  },
  {
    q: "What is a Certified in Financial Forensics (CFF) credential?",
    a: "The Certified in Financial Forensics (CFF) credential was authorized by the American Institute of Certified Public Accountants (AICPA) in May 2008. It recognizes CPAs with specialized forensic accounting expertise in areas including bankruptcy and insolvency, fraud detection, economic damages, family law, and financial statement misrepresentation. Dick Bufkin is one of very few practicing attorneys to hold this credential.",
  },
  {
    q: "Does the firm represent bankruptcy trustees?",
    a: "Yes. A substantial portion of Dick Bufkin's practice involves representing bankruptcy trustees in adversary proceedings to recover financial damages, property, or both. These often include fraudulent transfers, breaches of fiduciary duty, wrongful takings, and unjust enrichment claims.",
  },
  {
    q: "What types of cases does Dick Bufkin handle as an expert witness?",
    a: "Dick serves as both a consulting expert and a testifying expert witness in cases involving accounting and financial matters. Engagements include calculating and testifying to lost profits and economic damages, reviewing financial records and tax filings for litigation, controverting opposing experts' damage models, and designing demonstrative exhibits for trial.",
  },
  {
    q: "Where is The Bufkin Law Firm located?",
    a: "The Bufkin Law Firm is located at 126 W. Second Street, Mt. Pleasant, Texas 75455. The firm serves clients throughout Texas, including the Dallas–Fort Worth area. The direct phone line is (214) 415-1759.",
  },
  {
    q: "What practice areas does the firm cover?",
    a: "The firm's practice falls into three categories: (1) Litigation — business, family-law, probate, bankruptcy adversary proceedings, insurance contractual disputes, and economic-damages cases; (2) Financial Consulting and Testifying Expert Services — for other lawyers' trial teams; (3) Non-Litigation Legal Services — business formation, marital agreements, succession planning, asset protection, estate planning, and probate.",
  },
];

export const NAV_LINKS = [
  { label: "About", href: "/#about" },
  { label: "Practice Areas", href: "/practice-areas" },
  { label: "Insights", href: "/insights" },
  { label: "FAQ", href: "/#faq" },
  { label: "Contact", href: "/#contact" },
];

export const PRACTICE_AREA_CATEGORIES = [
  {
    id: "litigation",
    slug: "litigation",
    eyebrow: "Trial Practice",
    title: "Litigation",
    tagline: "Trial-level representation in matters where financial issues drive the outcome.",
    description:
      "Fifty-plus years of business, family-law, probate and creditor litigation — handled with the dual skill set of a trial lawyer and a Certified Public Accountant.",
    items: [
      "General business and breach of contract litigation and arbitration",
      "Partner, partnership, trust and fiduciary litigation",
      "Bankruptcy adversary proceedings on behalf of bankruptcy trustees",
      "Divorce, marital agreements and other financially oriented family law matters",
      "Insurance company, reinsurance, general agency and insurance executive contractual disputes",
      "Probate and other inheritance disputes",
      "Creditor representation of all types",
      "Asset protection engagements",
      "Non-bankruptcy debtor representation",
      "Cases involving lost profits and other economic damages",
    ],
    icon: "Gavel",
  },
  {
    id: "consulting",
    slug: "consulting",
    eyebrow: "Advisory & Expert Witness",
    title: "Financial Consulting & Testifying Expert Services",
    tagline: "Consulting and testifying expert work for other lawyers' trial teams.",
    description:
      "Engaged as both consulting expert and testifying expert in matters where financial analysis is decisive — from quantifying damages to discrediting opposing experts.",
    items: [
      "Expert testimony and opinions concerning accounting and financial matters, generally",
      "Consulting expert services concerning financially related matters",
      "Review and analysis of financial records for various litigation purposes",
      "Review and analysis of tax filings for various litigation purposes",
      "Determination and expert testimony establishing lost profits and other economic damages",
      "Expert testimony controverting and discrediting the damage contentions of opposing expert witnesses",
      "Creation of 'outside-the-box' demonstrative exhibits for more effective and simplified trial presentations",
      "Consultation and drafting assistance in making and responding to financially related discovery requests",
    ],
    icon: "Calculator",
  },
  {
    id: "non-litigation",
    slug: "non-litigation",
    eyebrow: "Transactional & Estate",
    title: "Non-Litigation Legal Services",
    tagline: "Transactional, planning, and probate work — with financial precision.",
    description:
      "Business formation, complex transactions, marital agreements, succession planning, asset protection, estate planning and probate. Always rooted in the same command of the numbers.",
    items: [
      "Business planning, entity formation and reorganizations",
      "Business transactions of unusual financial complexity",
      "Drafting of pre- and post-nuptial agreements, partition agreements, co-habitation agreements, etc.",
      "Successor ownership / management transition planning",
      "Asset protection planning",
      "Other debtor–creditor matters",
      "Contractual matters of unusual financial emphasis",
      "Tax and non-tax estate planning",
      "Both dispute and non-dispute oriented probate and inheritance matters",
    ],
    icon: "Scale",
  },
];

// Extended content for each practice area subpage
export const PRACTICE_AREA_DETAILS = {
  litigation: {
    extendedIntro:
      "When the verdict hinges on the financial story — who owed whom, how much, when, and why — the combined skill set of a trial lawyer and a CPA gives clients a singular advantage. For five decades Dick Bufkin has tried business, family-law, probate, and bankruptcy disputes where the financial issues were not sideshows. They were the case.",
    whenToEngage: [
      {
        title: "Financial issues drive the outcome",
        text: "Cases that turn on damages, fraudulent transfers, fiduciary breaches, or contract performance — not collateral disputes.",
      },
      {
        title: "Opposing experts must be challenged",
        text: "Where opposing damage calculations, valuations or accounting opinions need sophisticated cross-examination.",
      },
      {
        title: "Trustee & creditor representation",
        text: "Recovery actions in or alongside bankruptcy: fraudulent transfers, breach of fiduciary duty, unjust enrichment, wrongful takings.",
      },
      {
        title: "Family or probate with high-value assets",
        text: "Divorce, marital agreements, or inheritance disputes where asset characterization or tracing is decisive.",
      },
    ],
    whyItMatters: {
      title: "Why a CPA at counsel table changes the case",
      content:
        "Trial lawyers without accounting training have to translate the numbers twice — first to themselves, then to the jury. Dick reads the ledgers directly. That cuts weeks off case preparation, narrows issues earlier in discovery, and gives juries clean, defensible financial testimony from lead counsel — not just from a paid expert.",
    },
    notHandled: "The firm does not handle criminal or personal injury matters.",
    related: ["consulting", "non-litigation"],
  },
  consulting: {
    extendedIntro:
      "Many of the most complex commercial cases turn on a single financial question that the jury must understand and trust. Dick is engaged by other lawyers — and by their clients — to be the voice that makes that question clear. He works behind the scenes as a consulting expert and, when needed, takes the stand as a testifying expert.",
    whenToEngage: [
      {
        title: "Damages need to be proven — or disproven",
        text: "Lost profits, business valuations, calculation of economic harm, or rebuttal of the opposing side's damages model.",
      },
      {
        title: "The financials are a mess",
        text: "Forensic review of records, ledgers, tax filings, and reconstructions to surface what actually happened.",
      },
      {
        title: "Discovery requires a financial mind",
        text: "Drafting financial discovery requests, evaluating productions, and identifying the documents that matter.",
      },
      {
        title: "The jury needs to see it, not just hear it",
        text: "Designing demonstrative exhibits that simplify financial complexity for trial presentation.",
      },
    ],
    whyItMatters: {
      title: "A testifying expert who already thinks like a trial lawyer",
      content:
        "Most expert witnesses get cross-examined for the first time after years of writing reports. Dick has been cross-examining experts since the 1980s. He knows what kills credibility on the stand — and what builds it. That makes him as effective on rebuttal as he is on direct.",
    },
    notHandled: "Engagements are accepted on a case-by-case basis after conflict review.",
    related: ["litigation", "non-litigation"],
  },
  "non-litigation": {
    extendedIntro:
      "The best way to win a financial dispute is to never have one. Dick's non-litigation work is built on the same forensic mindset: structure deals, agreements and estates so the numbers work — and so they hold up when challenged. Decades at the litigation end of these same documents informs every comma.",
    whenToEngage: [
      {
        title: "Forming or restructuring a business",
        text: "Entity selection, capitalization, governance, and reorganization — designed with downstream tax and dispute risk in mind.",
      },
      {
        title: "Pre/post-nuptial or partition agreements",
        text: "Marital agreements drafted to actually survive the scrutiny they will receive if ever invoked.",
      },
      {
        title: "Succession and ownership transitions",
        text: "Owner-to-owner, generation-to-generation, or partner-to-partner transitions — structured to avoid future litigation.",
      },
      {
        title: "Estate planning & probate",
        text: "Tax and non-tax estate planning, probate, and inheritance work — dispute-oriented and otherwise.",
      },
    ],
    whyItMatters: {
      title: "Documents drafted by the lawyer who reads them in court",
      content:
        "Estate plans, partnership agreements, and complex transactions don't get tested until something goes wrong. Dick has spent fifty years on the other end — finding the missing comma, the silent contingency, the structure that looked clean on day one but collapsed under examination. He drafts to that standard.",
    },
    notHandled: "The firm focuses on financially complex transactional work and does not handle routine residential real-estate closings.",
    related: ["litigation", "consulting"],
  },
};

// Bio page data
export const BIO = {
  fullName: "Richard L. Bufkin",
  preferredName: "Dick Bufkin",
  title: "Attorney · Certified Public Accountant · Forensic Accountant",
  intro:
    "For more than fifty years, Dick Bufkin has stood at the unusual intersection of trial law and forensic accounting. He is one of very few practicing attorneys also holding the AICPA's Certified in Financial Forensics credential — a combination that makes him sought after as both trial counsel and as a testifying expert in financially driven cases.",
  philosophy:
    "Most cases come to Dick because the numbers tell a different story than the pleadings. His job is to find it, prove it, and make a judge or jury see it clearly.",
  credentials: [
    {
      label: "Attorney at Law",
      detail: "Texas State Bar. Fifty-plus years of business, family-law, probate and creditor litigation.",
    },
    {
      label: "Certified Public Accountant (CPA)",
      detail: "Active license. The accounting foundation underlying every engagement.",
    },
    {
      label: "AICPA Certified in Financial Forensics (CFF)",
      detail: "Awarded May 2008 by the American Institute of CPAs. One of very few attorneys nationwide holding this credential.",
    },
    {
      label: "Master of Laws in Taxation (LL.M.)",
      detail: "Southern Methodist University, Dedman School of Law.",
    },
    {
      label: "Master of Business Administration (MBA)",
      detail: "Foundational training in finance, management and economic analysis.",
    },
  ],
  experience: [
    {
      year: "50+ yrs",
      title: "Active Practice",
      detail: "Continuously practicing law in Texas — business, family-law, probate and bankruptcy litigation.",
    },
    {
      year: "May 2008",
      title: "AICPA CFF Awarded",
      detail: "Credential created by the AICPA; Dick earned it among the first cohort recognized for forensic accounting work.",
    },
    {
      year: "Ongoing",
      title: "Expert Witness Engagements",
      detail: "Regular service as consulting expert and testifying expert witness for other trial teams.",
    },
    {
      year: "Today",
      title: "Mt. Pleasant, Texas",
      detail: "Practice based in East Texas; clients served throughout Dallas–Fort Worth and statewide.",
    },
  ],
  representativeMatters: [
    "Bankruptcy trustee adversary proceedings — recovery of fraudulent transfers and breaches of fiduciary duty",
    "Construction contract & warranty disputes between owners, builders and contractors",
    "Insurance company, reinsurance and general agency contractual disputes",
    "Financially oriented divorce and marital-agreement matters",
    "Probate and inheritance disputes involving valuation, tracing and accounting",
    "Lost profits and economic damages litigation across industries",
    "Testifying expert engagements rebutting opposing damages models",
  ],
  doesNotHandle: [
    "Criminal defense",
    "Personal injury",
  ],
  recognition: [
    "Listed on Martindale.com",
    "Member, American Institute of Certified Public Accountants (AICPA)",
    "Member, State Bar of Texas",
    "AICPA Forensic and Valuation Services Section",
  ],
};

export const SERVICES = [
  {
    icon: Calculator,
    title: "Forensic Accounting",
    description:
      "Specialized analysis of complex financial records to uncover irregularities, calculate damages, and clarify what the numbers truly reveal.",
    items: ["Financial statement misrepresentation", "Asset tracing", "Damages quantification"],
  },
  {
    icon: ShieldAlert,
    title: "Fraud Detection",
    description:
      "Investigative work that exposes fraudulent transfers, wrongful takings, and concealed assets through rigorous accounting methodology.",
    items: ["Fraudulent transfers", "Breach of fiduciary duty", "Unjust enrichment"],
  },
  {
    icon: Gavel,
    title: "Business Litigation",
    description:
      "Trial-level representation in business disputes where financial issues are paramount — from contracts to construction claims.",
    items: ["Breached contracts", "Warranty disputes", "Owner vs. builder claims"],
  },
  {
    icon: FileSearch,
    title: "Bankruptcy & Insolvency",
    description:
      "Representation of bankruptcy trustees and creditors in actions to recover financial damages, property, or both.",
    items: ["Trustee representation", "Asset recovery", "Plaintiff claims"],
  },
  {
    icon: TrendingUp,
    title: "Economic Damages",
    description:
      "Calculation, analysis, and persuasive presentation of economic damages — lost profits, valuations, and financial harm.",
    items: ["Lost profits analysis", "Business valuations", "Financial harm modeling"],
  },
  {
    icon: BookOpen,
    title: "Expert Witness Services",
    description:
      "Consulting and testifying expert witness work that translates complex financial matters into clear, courtroom-ready testimony.",
    items: ["Consulting expert", "Testifying expert", "Trial team support"],
  },
];

export const PRACTICE_AREAS = [
  {
    icon: Building2,
    title: "Business & Financially Related Litigation",
    description:
      "Fifty-plus years representing businesses and individuals in disputes where the outcome turns on financial analysis — contracts, fiduciary duties, fraudulent transfers, and commercial claims.",
  },
  {
    icon: Calculator,
    title: "Forensic Accounting — Testifying & Consulting Expert",
    description:
      "One of very few attorneys holding the AICPA's Certified in Financial Forensics credential, Dick is engaged as both a consulting expert and a testifying expert in financial matters.",
  },
  {
    icon: Scale,
    title: "Transactions, Wills, Probate & Estate Planning",
    description:
      "Commercial law matters, business formation, transactional work, tax and non-tax estate planning, and probate — handled with the same financial precision.",
  },
];

export const FORENSIC_AREAS = [
  "Bankruptcy & Insolvency",
  "Fraud Detection",
  "Economic Damages",
  "Family Law",
  "Financial Statement Misrepresentation",
];

export const CLIENT_TYPES = [
  {
    icon: Scale,
    title: "Bankruptcy Trustees",
    description: "Court-appointed trustees pursuing recovery of estate assets and prosecution of avoidance actions.",
  },
  {
    icon: Users,
    title: "Plaintiffs Seeking Recovery",
    description: "Individuals and entities bringing claims to recover financial damages, property, or both.",
  },
  {
    icon: Building2,
    title: "Business Dispute Clients",
    description: "Owners, partners, and stakeholders in disputes with builders, contractors, partners, or counterparties.",
  },
  {
    icon: Gavel,
    title: "Trial Teams",
    description: "Other attorneys engaging Dick as a testifying or consulting expert to strengthen their trial teams.",
  },
];

export const STATS = [
  { value: "50+", label: "Years of Practice" },
  { value: "CPA", label: "Certified Public Accountant" },
  { value: "CFF", label: "Certified in Financial Forensics" },
  { value: "LL.M.", label: "Tax Law — SMU" },
];
